<template>
  <div>
    <div class="sm:hidden">
      <label for="tabs" class="sr-only">Select a tab</label>
      <!-- Use an "onChange" listener to redirect the user to the selected tab URL. -->
      <select
        id="tabs"
        name="tabs"
        class="
          block
          w-full
          focus:ring-indigo-500 focus:border-indigo-500
          border-gray-300
          rounded-md
        "
      >
        <option v-for="tab in tabs" :key="tab.name" :selected="tab.current">
          {{ tab.name }}
        </option>
      </select>
    </div>
    <div class="hidden sm:block">
      <nav
        class="relative z-0 rounded-lg shadow flex divide-x divide-gray-200"
        aria-label="Tabs"
      >
        <a
          v-for="(tab, tabIdx) in tabs"
          :key="tab.name"
          :href="tab.href"
          :class="[
            tab.current ? 'text-gray-900' : 'text-gray-500 hover:text-gray-700',
            tabIdx === 0 ? 'rounded-l-lg' : '',
            tabIdx === tabs.length - 1 ? 'rounded-r-lg' : '',
            'group relative min-w-0 flex-1 overflow-hidden bg-white py-4 px-4 text-sm font-medium text-center hover:bg-gray-50 focus:z-10',
          ]"
          :aria-current="tab.current ? 'page' : undefined"
        >
          <span>{{ tab.name }}</span>
          <span
            aria-hidden="true"
            :class="[
              tab.current ? 'bg-indigo-500' : 'bg-transparent',
              'absolute inset-x-0 bottom-0 h-0.5',
            ]"
          />
        </a>
      </nav>
    </div>
  </div>

  <div>
    <ul
      role="list"
      class="mt-3 grid grid-cols-1 gap-5 sm:gap-6 sm:grid-cols-2 lg:grid-cols-4"
    >
      <li
        v-for="project in projects"
        :key="project.name"
        class="col-span-1 flex shadow-sm rounded-md"
      >
        <div
          :class="[
            project.bgColor,
            'flex-shrink-0 flex items-center justify-center w-16 text-white text-sm font-medium rounded-l-md',
          ]"
        >
          {{ project.initials }}
        </div>
        <div
          class="
            flex-1 flex
            items-center
            justify-between
            border-t border-r border-b border-gray-200
            bg-white
            rounded-r-md
            truncate
          "
        >
          <div class="flex-1 px-4 py-2 text-sm truncate">
            <a
              :href="project.href"
              class="text-gray-900 font-medium hover:text-gray-600"
              >{{ project.name }}</a
            >
            <p class="text-gray-500">{{ project.members }} mediciones</p>
          </div>
          <div class="flex-shrink-0 pr-2">
            <button
              type="button"
              class="
                w-8
                h-8
                bg-white
                inline-flex
                items-center
                justify-center
                text-gray-400
                rounded-full
                bg-transparent
                hover:text-gray-500
                focus:outline-none
                focus:ring-2
                focus:ring-offset-2
                focus:ring-indigo-500
              "
            >
              <span class="sr-only">Open options</span>
              <DotsVerticalIcon class="w-5 h-5" aria-hidden="true" />
            </button>
          </div>
        </div>
      </li>
    </ul>
  </div>

  <form action="#" method="POST">
    <div class="shadow sm:rounded-md sm:overflow-hidden">
      <div class="bg-white py-6 px-4 space-y-6 sm:p-6">
        <div>
          <h3 class="text-lg leading-6 font-medium text-gray-900">
            Iluminación
          </h3>
          <p class="mt-1 text-sm text-gray-500">5/10 mediciones terminadas</p>
        </div>

        <div class="grid grid-cols-6 gap-6">
          <div class="col-span-6">
            <ui-divider class="my-6">
              <div class="relative flex items-center justify-between">
                <span class="pr-3 bg-white text-lg font-medium text-gray-900">
                  Mediciones
                </span>
                <button
                  type="button"
                  class="
                    inline-flex
                    items-center
                    shadow-sm
                    px-4
                    py-1.5
                    border border-green-300
                    text-sm
                    leading-5
                    font-medium
                    rounded-full
                    text-green-700
                    bg-white
                    hover:bg-green-50
                    focus:outline-none
                    focus:ring-2
                    focus:ring-offset-2
                    focus:ring-green-500
                  "
                >
                  <PlusIcon
                    class="-ml-1.5 mr-1 h-5 w-5 text-green-400"
                    aria-hidden="true"
                  />
                  <span>Agregar medición</span>
                </button>
              </div>
            </ui-divider>
          </div>

          <div class="flex flex-col col-span-6">
            <div class="-my-2 overflow-x-auto sm:-mx-6 lg:-mx-8">
              <div
                class="
                  py-2
                  align-middle
                  inline-block
                  min-w-full
                  sm:px-6
                  lg:px-8
                "
              >
                <div
                  class="
                    shadow
                    overflow-hidden
                    border-b border-gray-200
                    sm:rounded-lg
                  "
                >
                  <table class="min-w-full divide-y divide-gray-200">
                    <thead class="bg-gray-50">
                      <tr>
                        <th
                          scope="col"
                          class="
                            px-6
                            py-3
                            text-left text-xs
                            font-medium
                            text-gray-500
                            uppercase
                            tracking-wider
                          "
                        >
                          No medición
                        </th>
                        <th
                          scope="col"
                          class="
                            px-6
                            py-3
                            text-left text-xs
                            font-medium
                            text-gray-500
                            uppercase
                            tracking-wider
                          "
                        >
                          Periodo 1
                        </th>

                        <th
                          scope="col"
                          class="
                            px-6
                            py-3
                            text-left text-xs
                            font-medium
                            text-gray-500
                            uppercase
                            tracking-wider
                          "
                        >
                          Periodo 2
                        </th>
                        <th scope="col" class="relative px-6 py-3">
                          <span class="sr-only">Edit</span>
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr
                        v-for="(person, personIdx) in people"
                        :key="person.email"
                        :class="personIdx % 2 === 0 ? 'bg-white' : 'bg-gray-50'"
                      >
                        <td
                          class="
                            px-6
                            py-4
                            whitespace-nowrap
                            text-sm
                            font-medium
                            text-gray-900
                          "
                        >
                          {{ person.name }}
                        </td>
                        <td
                          class="
                            px-6
                            py-4
                            whitespace-nowrap
                            text-sm text-gray-500
                          "
                        >
                          {{ person.title }}
                        </td>
                        <td
                          class="
                            px-6
                            py-4
                            whitespace-nowrap
                            text-sm text-gray-500
                          "
                        >
                          {{ person.title }}
                        </td>
                        <td
                          class="
                            px-6
                            py-4
                            whitespace-nowrap
                            text-right text-sm
                            font-medium
                          "
                        >
                          <a
                            href="#"
                            class="text-indigo-600 hover:text-indigo-900"
                            >Editar</a
                          >
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>

          <div class="col-span-6 sm:col-span-3">
            <label
              for="first-name"
              class="block text-sm font-medium text-gray-700"
              >No. análisis</label
            >
            <input
              type="text"
              name="first-name"
              id="first-name"
              autocomplete="given-name"
              class="
                mt-1
                block
                w-full
                border border-gray-300
                rounded-md
                shadow-sm
                py-2
                px-3
                focus:outline-none focus:ring-indigo-500 focus:border-indigo-500
                sm:text-sm
              "
            />
          </div>

          <div class="col-span-6 sm:col-span-3">
            <label
              for="last-name"
              class="block text-sm font-medium text-gray-700"
              >Area, nombre o puesto de medición</label
            >
            <input
              type="text"
              name="last-name"
              id="last-name"
              autocomplete="family-name"
              class="
                mt-1
                block
                w-full
                border border-gray-300
                rounded-md
                shadow-sm
                py-2
                px-3
                focus:outline-none focus:ring-indigo-500 focus:border-indigo-500
                sm:text-sm
              "
            />
          </div>

          <div class="col-span-6">
            <label
              for="last-name"
              class="block text-sm font-medium text-gray-700"
              >Influencia de luz natural</label
            >
            <Switch
              v-model="enabled"
              :class="[
                enabled ? 'bg-indigo-600' : 'bg-gray-200',
                'relative inline-flex flex-shrink-0 h-6 w-11 border-2 border-transparent rounded-full cursor-pointer transition-colors ease-in-out duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500',
              ]"
            >
              <span
                aria-hidden="true"
                :class="[
                  enabled ? 'translate-x-5' : 'translate-x-0',
                  'pointer-events-none inline-block h-5 w-5 rounded-full bg-white shadow transform ring-0 transition ease-in-out duration-200',
                ]"
              />
            </Switch>
          </div>

          <div class="col-span-6 sm:col-span-3">
            <label
              for="last-name"
              class="block text-sm font-medium text-gray-700"
              >Periodo 1</label
            >
            <input
              type="text"
              name="last-name"
              id="last-name"
              autocomplete="family-name"
              class="
                mt-1
                block
                w-full
                border border-gray-300
                rounded-md
                shadow-sm
                py-2
                px-3
                focus:outline-none focus:ring-indigo-500 focus:border-indigo-500
                sm:text-sm
              "
            />
          </div>
          <div class="col-span-6 sm:col-span-3">
            <label
              for="last-name"
              class="block text-sm font-medium text-gray-700"
              >Periodo 2</label
            >
            <input
              type="text"
              name="last-name"
              id="last-name"
              autocomplete="family-name"
              class="
                mt-1
                block
                w-full
                border border-gray-300
                rounded-md
                shadow-sm
                py-2
                px-3
                focus:outline-none focus:ring-indigo-500 focus:border-indigo-500
                sm:text-sm
              "
            />
          </div>
          <div class="col-span-6 sm:col-span-3">
            <label
              for="last-name"
              class="block text-sm font-medium text-gray-700"
              >Periodo 3</label
            >
            <input
              type="text"
              name="last-name"
              id="last-name"
              autocomplete="family-name"
              class="
                mt-1
                block
                w-full
                border border-gray-300
                rounded-md
                shadow-sm
                py-2
                px-3
                focus:outline-none focus:ring-indigo-500 focus:border-indigo-500
                sm:text-sm
              "
            />
          </div>
          <div v-if="enabled == false" class="col-span-6 sm:col-span-3">
            <label
              for="last-name"
              class="block text-sm font-medium text-gray-700"
              >Periodo 4</label
            >
            <input
              type="text"
              name="last-name"
              id="last-name"
              autocomplete="family-name"
              class="
                mt-1
                block
                w-full
                border border-gray-300
                rounded-md
                shadow-sm
                py-2
                px-3
                focus:outline-none focus:ring-indigo-500 focus:border-indigo-500
                sm:text-sm
              "
            />
          </div>
          <div v-if="enabled == false" class="col-span-6 sm:col-span-3">
            <label
              for="last-name"
              class="block text-sm font-medium text-gray-700"
              >Periodo 5</label
            >
            <input
              type="text"
              name="last-name"
              id="last-name"
              autocomplete="family-name"
              class="
                mt-1
                block
                w-full
                border border-gray-300
                rounded-md
                shadow-sm
                py-2
                px-3
                focus:outline-none focus:ring-indigo-500 focus:border-indigo-500
                sm:text-sm
              "
            />
          </div>
          <div v-if="enabled == false" class="col-span-6 sm:col-span-3">
            <label
              for="last-name"
              class="block text-sm font-medium text-gray-700"
              >Periodo 6</label
            >
            <input
              type="text"
              name="last-name"
              id="last-name"
              autocomplete="family-name"
              class="
                mt-1
                block
                w-full
                border border-gray-300
                rounded-md
                shadow-sm
                py-2
                px-3
                focus:outline-none focus:ring-indigo-500 focus:border-indigo-500
                sm:text-sm
              "
            />
          </div>
        </div>
      </div>
    </div>
  </form>
  <form action="#" method="POST">
    <div class="shadow sm:rounded-md sm:overflow-hidden">
      <div class="bg-white py-6 px-4 space-y-6 sm:p-6">
        <div>
          <h3 class="text-lg leading-6 font-medium text-gray-900">Ruido</h3>
          <p class="mt-1 text-sm text-gray-500">0/8 mediciones terminadas</p>
        </div>

        <div class="grid grid-cols-6 gap-6">
          <div class="col-span-6">
            <ui-divider class="my-6">
              <div class="relative flex items-center justify-between">
                <span class="pr-3 bg-white text-lg font-medium text-gray-900">
                  Mediciones
                </span>
                <button
                  type="button"
                  class="
                    inline-flex
                    items-center
                    shadow-sm
                    px-4
                    py-1.5
                    border border-green-300
                    text-sm
                    leading-5
                    font-medium
                    rounded-full
                    text-green-700
                    bg-white
                    hover:bg-green-50
                    focus:outline-none
                    focus:ring-2
                    focus:ring-offset-2
                    focus:ring-green-500
                  "
                >
                  <PlusIcon
                    class="-ml-1.5 mr-1 h-5 w-5 text-green-400"
                    aria-hidden="true"
                  />
                  <span>Agregar medición</span>
                </button>
              </div>
            </ui-divider>
          </div>
        </div>
      </div>
    </div>
  </form>
  <form action="#" method="POST">
    <div class="shadow sm:rounded-md sm:overflow-hidden">
      <div class="bg-white py-6 px-4 space-y-6 sm:p-6">
        <div>
          <h3 class="text-lg leading-6 font-medium text-gray-900">
            Sulfato de sodio
          </h3>
          <p class="mt-1 text-sm text-gray-500">0/8 mediciones terminadas</p>
        </div>

        <div class="grid grid-cols-6 gap-6">
          <div class="col-span-6">
            <ui-divider class="my-6">
              <div class="relative flex items-center justify-between">
                <span class="pr-3 bg-white text-lg font-medium text-gray-900">
                  Mediciones
                </span>
                <button
                  type="button"
                  class="
                    inline-flex
                    items-center
                    shadow-sm
                    px-4
                    py-1.5
                    border border-green-300
                    text-sm
                    leading-5
                    font-medium
                    rounded-full
                    text-green-700
                    bg-white
                    hover:bg-green-50
                    focus:outline-none
                    focus:ring-2
                    focus:ring-offset-2
                    focus:ring-green-500
                  "
                >
                  <PlusIcon
                    class="-ml-1.5 mr-1 h-5 w-5 text-green-400"
                    aria-hidden="true"
                  />
                  <span>Agregar medición</span>
                </button>
              </div>
            </ui-divider>
          </div>
        </div>
      </div>
    </div>
  </form>
</template>

<script>
import { PlusIcon } from "@heroicons/vue/solid";
import { DotsVerticalIcon } from "@heroicons/vue/solid";
import { Switch } from "@headlessui/vue";

const people = [
  { name: "432123", title: "xxx" },
  { name: "432124", title: "xxx" },
  { name: "432125", title: "xxx" },
];

const projects = [
  {
    name: "Iluminación",
    initials: "I",
    href: "#",
    members: 16,
    bgColor: "bg-pink-600",
  },
  {
    name: "Ruido",
    initials: "R",
    href: "#",
    members: 12,
    bgColor: "bg-purple-600",
  },
  {
    name: "Sulfato de sodio",
    initials: "NA",
    href: "#",
    members: 4,
    bgColor: "bg-yellow-500",
  },
];
const tabs = [
  { name: "Producción", href: "#", current: true },
  { name: "Estrucutras", href: "#", current: false },
  { name: "Mantenimiento", href: "#", current: false },
  { name: "Almacén", href: "#", current: false },
];
export default {
  components: {
    PlusIcon,
    DotsVerticalIcon,
    Switch,
  },
  data() {
    return {
      enabled: false,
    };
  },
  setup() {
    return {
      tabs,
      projects,
      people,
    };
  },
};
</script>

<style>
</style>